/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { DefaultButton, PrimaryButton } from '@fluentui/react/lib/Button';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { BasicModal, BasicModalProps } from 'src/core/components/BasicModal';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { globalMessages } from 'src/core/utils/globalMessages';
import { withModalMounter } from 'src/core/utils/hocs/withModalMounter';

export type ConfirmationModalProps<T> = BasicModalProps<object> & {
    /**
     * The projected elements passed from the consumer between
     * the dialog tags.
     */
    children?: React.ReactNode;

    /**
     * Indicates that the prompt is not valid so
     * it can not be submitted but can be canceled.
     */
    invalid?: boolean;

    /**
     * Optional text for submit button.
     */
    submitButtonText?: string;

    /**
     * A callback for when the modal 'Done' button is clicked.
     */
    onConfirm?: (data?: T) => void;
};

const messages = defineMessages({
    okButton: {
        id: 'core.confirmationModal.okButton',
        defaultMessage: 'Ok'
    }
});

/**
 * Represents a confirmation prompt modal
 * @param props The properties passed to the modal.
 */
export const ConfirmationModal = withModalMounter((props: ConfirmationModalProps<object>) => {
    const { minWidth = 500, onConfirm, onDismiss, children, disabled, submitButtonText, invalid = false } = props;
    const { localize } = useLocalization();

    /**
     * Calls the dismiss callback on modal close button click or 'Cancel'
     * button click only if the disabled flag is false.
     */
    const dismissModal = (): void => {
        if (!disabled && onDismiss) {
            onDismiss();
        }
    };

    /**
     * Renders a custom footer for the confirmation modal.
     */
    const onRenderFooter = () => (
        <>
            <PrimaryButton
                text={submitButtonText ?? localize(messages.okButton)}
                disabled={disabled || invalid}
                onClick={onConfirm}
                data-automation-id="confirmButton"
            />
            <DefaultButton
                disabled={disabled}
                onClick={dismissModal}
                styles={{ root: { marginLeft: 10 } }}
                text={localize(globalMessages.cancel)}
            />
        </>
    );

    return (
        <BasicModal {...props} minWidth={minWidth} onRenderFooter={onRenderFooter}>
            {children}
        </BasicModal>
    );
});
